import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import * as api from '../services/api';
import './AiChat.css';

function AiChat({ externalOpen, onExternalToggle }) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const effectiveOpen = externalOpen !== undefined ? externalOpen : isOpen;
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hej! Jeg er din FastTrack-assistent. Jeg kan hjælpe med spørgsmål om dine bookinger, foreslå nye bookinger baseret på din historik, eller give dig overblik over dine leverancer. Hvad kan jeg hjælpe med?' },
  ]);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoaded, setAlertsLoaded] = useState(false);

  const getDismissed = () => {
    try { return JSON.parse(localStorage.getItem('dismissed_alerts') || '[]'); } catch { return []; }
  };
  const dismissAlert = (index) => {
    const msg = alerts[index]?.message;
    if (msg) {
      const dismissed = [...getDismissed(), msg];
      localStorage.setItem('dismissed_alerts', JSON.stringify(dismissed));
    }
    setAlerts(prev => prev.filter((_, j) => j !== index));
  };
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch proactive alerts on mount
  useEffect(() => {
    if (!alertsLoaded) {
      setAlertsLoaded(true);
      api.getAiProactive().then(data => {
        if (data.alerts && data.alerts.length > 0) {
          const dismissed = getDismissed();
          const filtered = data.alerts.filter(a => !dismissed.includes(a.message));
          setAlerts(filtered);
        }
      }).catch(() => {});
    }
  }, [alertsLoaded]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const conversation = messages.filter(m => m.role !== 'system');
      const data = await api.sendAiChat(userMsg, conversation);
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Beklager, der opstod en fejl. Prøv igen.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <div className="ai-chat-fab-wrapper" onClick={() => {
        if (onExternalToggle) onExternalToggle();
        else setIsOpen(!isOpen);
      }}>
        {!effectiveOpen && (
          <>
            {alerts.length > 0 && <span className="ai-alert-badge">{alerts.length}</span>}
            <div className="ai-chat-bubble">{alerts.length > 0 ? `${alerts.length} ny${alerts.length === 1 ? 't' : 'e'} forslag` : t('aiMate')}</div>
          </>
        )}
        <button className="ai-chat-fab" title="AI Assistent (Alt+C)">
          {effectiveOpen ? '✕' : (
            <svg className="truck-icon" viewBox="0 0 40 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="4" width="24" height="16" rx="2" fill="#007566"/>
              <rect x="2" y="6" width="20" height="12" rx="1" fill="white" opacity="0.2"/>
              <text x="12" y="15" textAnchor="middle" fill="white" fontSize="6" fontWeight="800" fontFamily="Arial">OLT</text>
              <path d="M24 10H32C34 10 36 12 36 14V20H24V10Z" fill="#005f53"/>
              <rect x="26" y="12" width="8" height="5" rx="1" fill="#8ED8CC" opacity="0.6"/>
              <rect x="0" y="20" width="36" height="3" rx="1" fill="#333"/>
              <circle cx="8" cy="24" r="4" fill="#333"/>
              <circle cx="8" cy="24" r="2" fill="#999"/>
              <circle cx="30" cy="24" r="4" fill="#333"/>
              <circle cx="30" cy="24" r="2" fill="#999"/>
            </svg>
          )}
        </button>
      </div>

      {effectiveOpen && (
        <div className="ai-chat-window">
          <div className="ai-chat-header">
            <span className="ai-chat-title">FastTrack AI Assistent</span>
            <button className="ai-chat-close" onClick={() => { if (onExternalToggle) onExternalToggle(); else setIsOpen(false); }}>✕</button>
          </div>
          <div className="ai-chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`ai-chat-msg ai-chat-msg--${msg.role}`}>
                {msg.content}
              </div>
            ))}
            {alerts.length > 0 && (
              <div className="ai-alerts">
                {alerts.map((a, i) => (
                  <div key={i} className={`ai-alert ai-alert--${a.type}`}>
                    <span className="ai-alert-icon">
                      {a.type === 'suggestion' ? '💡' : a.type === 'anomaly' ? '⚠️' : '📌'}
                    </span>
                    <span className="ai-alert-text">{a.message}</span>
                    <button className="ai-alert-dismiss" onClick={() => dismissAlert(i)}>✕</button>
                  </div>
                ))}
              </div>
            )}
            {loading && (
              <div className="ai-chat-msg ai-chat-msg--assistant ai-chat-typing">
                {t('aiThinking')}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="ai-chat-input-row">
            <input
              type="text"
              className="ai-chat-input"
              placeholder={t("aiPlaceholder")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <button className="ai-chat-send" onClick={handleSend} disabled={loading || !input.trim()}>
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default AiChat;
